import axios from "axios";
import config from "../config";
import { createCouponError } from "../utils/coupon.utils";
// import store from "../store/store";

let accessToken = "";
let logoutCallback = null;
const PUBLIC_COUPON_OFFER_PATH = "/coupons/web/offers/detail";

// 创建 Axios 实例
const httpClient = axios.create({
  baseURL: config.apiBaseUrl, // 替换为你的 API 地址
});

function _post(path, data, config) {
  // access token 是 okshop 登入後回傳的 access token, 並非 line 的 access token
  const payload = {
    data: JSON.stringify(data),
  };
  if (config === undefined) {
    return httpClient.post(path, payload);
  }
  return httpClient.post(path, payload, config);
}

function _get(path) {
  return httpClient.get(path);
}

httpClient.interceptors.response.use(
  (response) => {
    // 成功處理 API 回應
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const requestPath = error?.config?.url || error?.response?.config?.url;
    if (typeof requestPath === "string" && requestPath.startsWith("/coupons/web/")) {
      // Axios error 內含 JWT 與 request body；優惠券端點只記錄可排查的非敏感欄位
      const endpoint = [
        "/coupons/web/templates/list",
        "/coupons/web/templates/detail",
        "/coupons/web/offers/detail",
        "/coupons/web/offers/claim",
        "/coupons/web/member-coupons/list",
        "/coupons/web/member-coupons/detail",
        "/coupons/web/order-coupons/select",
        "/coupons/web/order-coupons/validate",
        "/coupons/web/order-coupons/remove",
      ].includes(requestPath)
        ? requestPath
        : "/coupons/web/*";
      console.error("Coupon API error", {
        endpoint,
        status,
        error_code: error?.response?.data?.data?.error_code,
        request_id: error?.response?.data?.request_id,
      });
    } else {
      // 其他 API 保持原有日誌行為
      console.error("API error", error);
      if (status) {
        console.error("API error, status: ", status);
      }
    }
    // 429 為限流，不是登入失效，交由呼叫端提示稍後再試
    if (
      requestPath !== PUBLIC_COUPON_OFFER_PATH &&
      status > 400 && status < 500 && status !== 429
    ) {
      // 執行登出
      if (logoutCallback) {
        logoutCallback();
      }
    }

    return Promise.reject(error);
  }
);

// 请求拦截器，用于在每个请求前添加逻辑
httpClient.interceptors.request.use(
  (config) => {
    // console.log('config', config);
    if (config.url === PUBLIC_COUPON_OFFER_PATH) {
      // 公開詳情只以 Offer 辨認店家，不附殘留 JWT 或既有 Authorization。
      Object.keys(config.headers).forEach((name) => {
        if (name.toLowerCase() === "authorization") delete config.headers[name];
      });
    } else if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    config.headers["Content-Type"] = "application/x-www-form-urlencoded";
    // 可以在请求发送前添加 token 等逻辑
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const login = async (data) => _post("/members/login", data);
export const exchangeLineCode = async (data) => _post("/members/line/token", data);
export const joinMember = async () => _get("/members/join", {});
export const getMemberInfo = async () => _get("/members/info");
export const updateMemberInfo = async (data) => _post("/members/update", data);
export const checkEinvoiceCode = async (data, config) =>
  _post("/einvoice/line/carrier_check", data, config);
export const deleteAddress = async (data) =>
  _post("/members/address_del", data);
export const getCities = async () => _get("/members/get_cities");
export const getCityAreas = async () => _get("/members/get_cityareas");
export const getStoreInfo = () => _get("/members/store_info");
export const pointHistory = async (data) =>
  _post("/point_report/point_history", data);
export const memberPreDeduct = async (data) =>
  _post("/point_report/member_pre_deduct", data);
export const getConsumptionInfo = async () =>
  _post("/orders_history/consumption_info");
export const ordersHistory = async (data) => _post("/orders_history", data);
export const storeChange = (data) => _post("/members/change", data);
export const setAccessToken = (value) => (accessToken = value);
export const setLogoutCallback = (callback) => (logoutCallback = callback);
export const getStoreCoupons = async (data) => _post("/coupons", data);
export const getCouponInfo = async (data) =>
  _post("/coupons/coupon_info", data);
export const getMemberCoupons = async (data) =>
  _post("/coupons/member_coupons", data);
export const getMemberCouponInfo = async (data) =>
  _post("/coupons/member_coupon_info", data);
export const claimCoupon = async (data) => _post("/coupons/get_coupon", data);

// 會員優惠券 v2：母券 template_uuid 只查詳情，claim 只送詳情回傳的 offer_uuid，兩者不可互換
export const listPublicCouponTemplatesV2 = ({ page = 1 } = {}) =>
  postCouponV2("/coupons/web/templates/list", { page, page_size: 20 });

export const getPublicCouponTemplateV2 = (templateUuid) =>
  postCouponV2("/coupons/web/templates/detail", {
    template_uuid: templateUuid,
  });

export const getCouponOfferV2 = (offerUuid) =>
  postCouponV2("/coupons/web/offers/detail", {
    offer_uuid: offerUuid,
  });

export const listMemberCouponsV2 = ({ page = 1, pageSize = 20, status } = {}) =>
  postCouponV2("/coupons/web/member-coupons/list", {
    page,
    page_size: pageSize,
    ...(status ? { status } : {}),
  });

export const getMemberCouponV2 = (memberCouponUuid) =>
  postCouponV2("/coupons/web/member-coupons/detail", {
    member_coupon_uuid: memberCouponUuid,
  });

export const validateOrderCouponV2 = ({ memberCouponUuid }) =>
  postCouponV2("/coupons/web/order-coupons/validate", {
    member_coupon_uuid: memberCouponUuid,
  }, undefined, true);

export const claimCouponV2 = (offerUuid, idempotencyKey) =>
  postCouponV2(
    "/coupons/web/offers/claim",
    { offer_uuid: offerUuid },
    {
      headers: { "Idempotency-Key": idempotencyKey },
    }
  );

// 會員優惠券錯誤只傳中文訊息給 UI，不帶 Axios config／request body
async function postCouponV2(path, data, requestConfig, requireSuccessCode = false) {
  const form = new URLSearchParams();
  form.set("data", JSON.stringify(data));
  let response;
  try {
    response = await httpClient.post(path, form.toString(), requestConfig);
  } catch (error) {
    // 業務錯誤實際回 HTTP 400（限流 429），body 為 code=FAILED＋data.error_code
    const status = error?.response?.status;
    const envelope = error?.response?.data;
    if (envelope?.code === "FAILED" || status === 429) {
      throw createCouponError(envelope?.data?.error_code, status, {
        ...envelope?.data,
        msg: typeof envelope?.msg === "string" && envelope.msg.trim()
          ? envelope.msg : envelope?.data?.msg,
        request_id: envelope?.request_id,
        retryAfter: error.response?.headers?.["retry-after"],
      });
    }
    throw new Error(
      error?.response
        ? "優惠券服務暫時無法使用，請稍後再試"
        : "連線失敗，請確認網路後再試"
    );
  }
  if (response?.data?.code === "FAILED") {
    throw createCouponError(response.data.data?.error_code, response.status, {
      ...response.data.data,
      msg: typeof response.data.msg === "string" && response.data.msg.trim()
        ? response.data.msg : response.data.data?.msg,
      request_id: response.data.request_id,
    });
  }
  if (requireSuccessCode && response?.data?.code !== "SUCCESS") {
    throw new Error("優惠券確認失敗，請稍後再試");
  }
  const payload = response?.data?.data;
  if (payload?.msg === "Failed") {
    const message =
      typeof payload.err_msg === "string" && payload.err_msg.trim()
        ? payload.err_msg
        : "優惠券操作失敗，請稍後再試";
    throw new Error(message);
  }
  // schema_version 僅作版本標記；實際欄位由各呼叫者驗證。
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("優惠券資料不完整，請稍後再試");
  }
  return payload;
}
export const createOnlineOrder = async (data) =>
  _post("online_orders/create", data);
export const directPrint = async (data) => _post("/members/direct_print", data);
export const getOnlineOrderInfo = async (data) =>
  _post("online_orders/info", data);
export const getOnlineOrderList = async (data) =>
  _post("online_orders/info_list", data);
export const cancelOnlineOrder = async (data) =>
  _post("online_orders/cancel", data);
export const getProductImages = async (data) =>
  _post("product_images/list", data);
export const getPaymentMethods = async (data) =>
  _post("payments/store_payment_methods_info_list_web", data);
export const getPaymentMethodByTypeAndId = async (data) =>
  _post("payments/store_payment_methods_info_web", data);
export const linePayRequest = async (data) =>
  _post("payments/linepay_request", data);
export const linePayConfirm = async (data) =>
  _post("payments/linepay_confirm", data);
export const linePayCancel = async (data) =>
  _post("payments/linepay_cancel", data);
// 街口支付 OnlinePay：Entry 建立付款、Inquiry 查詢最終狀態
// 街口沒有對應 linepay_confirm 的端點，付款結果一律由 Inquiry 決定
export const jkoPayEntry = async (data) =>
  _post("payments/jkopay/online/entry", data);
export const jkoPayInquiry = async (data) =>
  _post("payments/jkopay/online/inquiry", data);
export const getProductDescriptions = async (data) =>
  _post("/product_desc/list_web", data);
export const getRewardCardList = async (data) =>
  _post("/reward_point/web/reward_card_list", data);
export const getRewardPointProductsList = async (data) =>
  _post("/reward_point/web/reward_point_products_list", data);
export const getMemberTotalRewardPoints = async (data) =>
  _post("/reward_point/web/member_total_reward_points", data);
export const getMemberRewardPointsHistory = async (data) =>
  _post("/reward_point/web/member_reward_points_history", data);
export const getStoreServicesList = async () =>
  _post("/stores/web/store_services_list");
export const getMemberStores = async (data) => _post("/members/stores", data);

// 商品保管 / 領取 API
export const getDepositProductsInfoList = async (data) =>
  _post("/deposit_prods/web/deposit_prods_info_list", data);
export const getDepositProductsLogList = async (data) =>
  _post("/deposit_prods/web/deposit_prods_log_list", data);

// Menu Hive API
// 讀取型、冪等、外層有 withRetry 保護，設單支 8 秒逾時快速失敗。
// timeout 僅作用於 menu_hive 端點；付款、送單等寫入型 API 不設 timeout，
// 避免「後端已成功、前端逾時重試」造成重複下單。
// 8 秒 × 3 次嘗試 + 退避 ≈ 25.3 秒，低於 Ordering 的 30 秒整體上限。
const _menuHiveParams = { menu_id: "0", page: 0, page_size: 999 };
const MENU_HIVE_TIMEOUT = 8000;
const _menuHivePost = (path) =>
  httpClient.post(
    path,
    { data: JSON.stringify(_menuHiveParams) },
    { timeout: MENU_HIVE_TIMEOUT }
  );
export const getMenuHiveCategories = async () =>
  _menuHivePost("/menu_hive/web/goods_categories_list");
export const getMenuHiveProducts = async () =>
  _menuHivePost("/menu_hive/web/products_list");
export const getMenuHiveProductSpecs = async () =>
  _menuHivePost("/menu_hive/web/product_specs_list");
export const getMenuHiveCombos = async () =>
  _menuHivePost("/menu_hive/web/combo_list");
export const getMenuHiveComboSubs = async () =>
  _menuHivePost("/menu_hive/web/combo_subs_list");
export const getMenuHiveComboSubAlt = async () =>
  _menuHivePost("/menu_hive/web/combo_sub_alt_list");
export const getMenuHiveComboSubAny = async () =>
  _menuHivePost("/menu_hive/web/combo_sub_any_list");
export const getMenuHiveSheetSpecArrangements = async () =>
  _menuHivePost("/menu_hive/web/sheet_spec_arrangements_list");
export const getMenuHiveServeAreas = async () =>
  _menuHivePost("/menu_hive/web/serve_areas_list");
export const getMenuHiveServeTables = async () =>
  _menuHivePost("/menu_hive/web/serve_tables_list");
export const getMenuHiveStockItems = async () =>
  _menuHivePost("/menu_hive/web/stock_items_list");
export const getMenuHiveRetailGroups = async () =>
  _menuHivePost("/menu_hive/web/retail_groups_list");

// 店家圖片 API
export const getStoreFileList = async () => _get("/stores/web/files");
export const downloadStoreFile = async (fileName) => {
  const formData = new FormData();
  formData.append("data", JSON.stringify({ filename: fileName }));

  return httpClient.post("/stores/web/download", formData, {
    responseType: "blob",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};
